import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties, DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { setEstado, setTaskStage } from './api';
import type { Tag, AssigneeReceipt } from './api';
import { DoubleCheck } from './TaskCard';
import { useDialog } from '../ui/Dialogs';
import type { Stage, Task, TaskEstado } from './types';

// Tablero (kanban) de la Bandeja. Las columnas son: "Sin etapa" + las etapas
// del espacio (subdividen En curso) + Resuelto + Terminada. Arrastrar entre
// etapas cambia la etapa (solo el asignado); arrastrar a Resuelto/Terminada
// cambia el estado con las mismas reglas que los botones del hilo.

// Claves de las columnas fijas; las de etapa usan el id de la etapa.
const COL_NONE = 'none';
const COL_RESUELTO = 'resuelto';
const COL_TERMINADA = 'terminada';

// La columna Terminada solo enseña lo cerrado recientemente, para que no
// crezca sin límite. El resto sigue en la vista de lista.
const TERMINADA_DAYS = 30;

interface Column {
  key: string;
  title: string;
  color: string;
}

interface Props {
  tasks: Task[];
  stages: Stage[];
  userId: string | undefined;
  tagsByTask: Record<string, Tag[]>;
  receipts: Record<string, AssigneeReceipt>;
  // Ids de hilos con novedades sin leer (solo la vista "Todo" los conoce).
  unreadIds?: Set<string>;
  onChanged?: () => void;
}

export default function BoardView({ tasks, stages, userId, tagsByTask, receipts, unreadIds, onChanged }: Props) {
  const { alert } = useDialog();
  const navigate = useNavigate();
  const [dragId, setDragId] = useState<string | null>(null);
  const [overCol, setOverCol] = useState<string | null>(null);
  // Movimientos aplicados en local hasta que llega el refresco del servidor.
  const [pending, setPending] = useState<Record<string, { estado: TaskEstado; stage_id: string | null }>>({});

  useEffect(() => { setPending({}); }, [tasks]);

  const stageIds = useMemo(() => new Set(stages.map((s) => s.id)), [stages]);

  const columns = useMemo<Column[]>(() => [
    { key: COL_NONE, title: stages.length > 0 ? 'Sin etapa' : 'En curso', color: stages.length > 0 ? '#94a3b8' : '#2563eb' },
    ...stages.map((s) => ({ key: s.id, title: s.nombre, color: s.color })),
    { key: COL_RESUELTO, title: 'Resuelto', color: '#f59e0b' },
    { key: COL_TERMINADA, title: 'Terminada', color: '#16a34a' },
  ], [stages]);

  const view = useMemo(
    () => tasks.map((t) => (pending[t.id] ? { ...t, ...pending[t.id] } : t)),
    [tasks, pending],
  );

  // Etapa efectiva: una etapa que ya no existe cuenta como "Sin etapa".
  const stageOf = (t: Task): string | null => (t.stage_id && stageIds.has(t.stage_id) ? t.stage_id : null);
  const columnOf = (t: Task): string =>
    t.estado === 'resuelto' ? COL_RESUELTO : t.estado === 'terminada' ? COL_TERMINADA : stageOf(t) ?? COL_NONE;

  const byColumn = useMemo(() => {
    const cutoff = Date.now() - TERMINADA_DAYS * 86400000;
    const out: Record<string, Task[]> = {};
    columns.forEach((c) => { out[c.key] = []; });
    view.forEach((t) => {
      const key = columnOf(t);
      if (key === COL_TERMINADA && new Date(t.updated_at).getTime() < cutoff) return;
      out[key]?.push(t);
    });
    return out;
    // columnOf depende solo de las etapas, ya cubiertas por `columns`.
  }, [view, columns]);

  const canDrop = (t: Task, col: string): boolean => {
    if (!userId || columnOf(t) === col) return false;
    const isCreator = t.created_by === userId;
    const isAssignee = t.assigned_to === userId;
    if (col === COL_RESUELTO) return isAssignee && t.estado === 'en_curso';
    if (col === COL_TERMINADA) return isCreator;
    // Columna de etapa: la mueve el asignado (o el creador si no hay asignado).
    const canStage = t.assigned_to ? isAssignee : isCreator;
    if (t.estado === 'en_curso') return canStage;
    // Desde Resuelto/Terminada hay que poder devolverlo a En curso; quien no
    // puede cambiar la etapa solo puede soltarlo en la que ya tenía.
    const canReopen = t.estado === 'resuelto' ? isCreator || isAssignee : isCreator;
    const target = col === COL_NONE ? null : col;
    return canReopen && (canStage || stageOf(t) === target);
  };

  const dragged = dragId ? view.find((t) => t.id === dragId) ?? null : null;

  const move = async (t: Task, col: string) => {
    const estado: TaskEstado = col === COL_RESUELTO ? 'resuelto' : col === COL_TERMINADA ? 'terminada' : 'en_curso';
    const stage = estado === 'en_curso' ? (col === COL_NONE ? null : col) : stageOf(t);
    setPending((p) => ({ ...p, [t.id]: { estado, stage_id: stage } }));
    try {
      if (estado !== t.estado) await setEstado(t.id, estado);
      if (stage !== stageOf(t)) await setTaskStage(t.id, stage);
      onChanged?.();
    } catch (err) {
      setPending((p) => { const { [t.id]: _drop, ...rest } = p; return rest; });
      alert({ title: 'No se pudo mover el hilo', message: err instanceof Error ? err.message : undefined });
    }
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>, col: string) => {
    if (!dragged || !canDrop(dragged, col)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (overCol !== col) setOverCol(col);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>, col: string) => {
    e.preventDefault();
    const t = dragged;
    setDragId(null);
    setOverCol(null);
    if (t && canDrop(t, col)) move(t, col);
  };

  return (
    <div style={styles.board}>
      {columns.map((c) => {
        const list = byColumn[c.key] ?? [];
        const droppable = dragged ? canDrop(dragged, c.key) : false;
        const dimmed = !!dragged && !droppable && columnOf(dragged) !== c.key;
        return (
          <div
            key={c.key}
            onDragOver={(e) => onDragOver(e, c.key)}
            onDragLeave={() => { if (overCol === c.key) setOverCol(null); }}
            onDrop={(e) => onDrop(e, c.key)}
            style={{
              ...styles.column,
              opacity: dimmed ? 0.45 : 1,
              outline: overCol === c.key ? '2px solid #2563eb' : droppable ? '2px dashed #cbd5e1' : '2px solid transparent',
            }}
          >
            <div style={styles.colHead}>
              <span style={{ ...styles.colDot, background: c.color }} />
              <span style={styles.colTitle} title={c.title}>{c.title}</span>
              <span style={styles.colCount}>{list.length}</span>
            </div>
            <div style={styles.colBody}>
              {list.map((t) => {
                const draggable = columns.some((col) => canDrop(t, col.key));
                return (
                  <BoardCard
                    key={t.id}
                    task={t}
                    tags={tagsByTask[t.id]}
                    readReceipt={receipts[t.id]}
                    unread={unreadIds?.has(t.id) ?? false}
                    draggable={draggable}
                    dragging={dragId === t.id}
                    onOpen={() => navigate(`/tareas/${t.id}`)}
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', t.id);
                      setDragId(t.id);
                    }}
                    onDragEnd={() => { setDragId(null); setOverCol(null); }}
                  />
                );
              })}
              {list.length === 0 && <div style={styles.colEmpty}>—</div>}
            </div>
            {c.key === COL_TERMINADA && (
              <div style={styles.colFoot}>Últimos {TERMINADA_DAYS} días</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface CardProps {
  task: Task;
  tags?: Tag[];
  readReceipt?: AssigneeReceipt;
  unread: boolean;
  draggable: boolean;
  dragging: boolean;
  onOpen: () => void;
  onDragStart: (e: DragEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
}

// Tarjeta compacta del tablero. Es un div (no el <button> de TaskCard) porque
// Firefox no inicia el arrastre desde un botón.
function BoardCard({ task, tags = [], readReceipt, unread, draggable, dragging, onOpen, onDragStart, onDragEnd }: CardProps) {
  const creator = task.creator ?? null;
  const assignee = task.assignee ?? null;
  const person = creator ?? assignee;
  const who = creator
    ? assignee && assignee.id !== creator.id
      ? `${creator.nombre} → ${assignee.nombre}`
      : !assignee
        ? `${creator.nombre} → General`
        : creator.nombre
    : assignee?.nombre ?? 'General';
  const fecha = formatDate(task.fecha_vencimiento ?? task.created_at);

  return (
    <div
      role="link"
      tabIndex={0}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}
      style={{
        ...styles.card,
        borderLeft: unread ? '3px solid #2563eb' : '1px solid #e2e8f0',
        paddingLeft: unread ? 10 : 12,
        opacity: dragging ? 0.4 : 1,
        cursor: draggable ? 'grab' : 'pointer',
      }}
    >
      <div style={{ ...styles.subject, ...(unread ? styles.subjectUnread : null) }}>{task.asunto}</div>
      {tags.length > 0 && (
        <div style={styles.tagRow}>
          {tags.map((tg) => (
            <span key={tg.id} style={styles.tag}>
              <span style={{ ...styles.tagDot, background: tg.color }} />
              {tg.nombre}
            </span>
          ))}
        </div>
      )}
      <div style={styles.cardFoot}>
        <span style={{ ...styles.avatar, background: person?.color ?? '#94a3b8' }}>
          {person ? (person.nombre.trim()[0] ?? '?').toUpperCase() : '#'}
        </span>
        <span style={styles.who} title={who}>{who}</span>
        <span style={styles.date}>{fecha}</span>
        {readReceipt && (
          <span
            style={{ display: 'inline-flex' }}
            title={readReceipt.read ? 'Visto por el asignado' : 'El asignado aún no lo ha leído'}
          >
            <DoubleCheck color={readReceipt.read ? '#16a34a' : '#cbd5e1'} />
          </span>
        )}
      </div>
    </div>
  );
}

function formatDate(input: string | null | undefined): string {
  if (!input) return '';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}`;
}

const styles: Record<string, CSSProperties> = {
  board: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    overflowX: 'auto',
    paddingBottom: 16,
  },
  column: {
    width: 272,
    minWidth: 272,
    background: '#f1f5f9',
    borderRadius: 12,
    padding: 10,
    outlineOffset: -2,
    transition: 'opacity .12s ease',
  },
  colHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '4px 4px 10px',
  },
  colDot: { width: 9, height: 9, minWidth: 9, borderRadius: '50%' },
  colTitle: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    fontWeight: 700,
    color: '#0f172a',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  colCount: { fontSize: 12, fontWeight: 600, color: '#64748b' },
  colBody: { display: 'flex', flexDirection: 'column', gap: 8, minHeight: 48 },
  colEmpty: { color: '#cbd5e1', fontSize: 13, textAlign: 'center', padding: '12px 0' },
  colFoot: { fontSize: 11, color: '#94a3b8', textAlign: 'center', paddingTop: 10 },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    padding: '10px 12px',
    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
    userSelect: 'none',
  },
  subject: {
    fontSize: 13.5,
    color: '#1e293b',
    lineHeight: 1.4,
    wordBreak: 'break-word',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  subjectUnread: { fontWeight: 600, color: '#0f172a' },
  tagRow: { display: 'flex', flexWrap: 'wrap', gap: 5 },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    background: '#f1f5f9',
    borderRadius: 999,
    padding: '1px 8px',
    fontSize: 10.5,
    fontWeight: 600,
    color: '#475569',
  },
  tagDot: { width: 6, height: 6, minWidth: 6, borderRadius: '50%' },
  cardFoot: { display: 'flex', alignItems: 'center', gap: 6 },
  avatar: {
    width: 20,
    height: 20,
    minWidth: 20,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontWeight: 700,
    fontSize: 10,
  },
  who: {
    flex: 1,
    minWidth: 0,
    fontSize: 11.5,
    color: '#64748b',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  date: { fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' },
};
